import React from "react";
import BgImg from "gatsby-background-image";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import { Flex, Box } from "@src/components/Boxes";
import { Text } from "@src/components/Text";
import { PlayIcon, OverflowIcon, LinkIcon, FileIcon } from "@classdojo/web/nessie/icons";
import { theme, BodyText, DeprecatedDetailText, Space, Button } from "@src/components/nessie-web";
import { DropDown, DropDownItem } from "@src/components/Dropdown";
import Container from "@src/components/Container";
import { css } from "@emotion/react";
import Translate from "@src/components/translation/Translate";

const EpisodeContainer = ({ children }) => (
  <Flex
    justifyContent="space-between"
    padding="12px 0"
    alignItems={["start", "center"]}
    width="100%"
    borderTop="1px solid"
    borderTopColor={theme.colors.taro30}
    flexDirection={["column", "row"]}
  >
    {children}
  </Flex>
);

const SectionTitle = ({ children, image }) => (
  <BgImg
    as="div"
    fixed={image}
    style={{
      width: "100%",
      backgroundPosition: "top left",
      backgroundRepeat: "no-repeat",
      backgroundSize: "65px",
      transform: "translateX(-24px)",
      height: "fit-content",
    }}
  >
    <Text
      fontSize={["24px", "30px"]}
      fontWeight="700"
      lineHeight={1.2}
      color={theme.colors.taro90}
      textAlign="left"
      padding="12px 0 12px 24px"
    >
      {children}
    </Text>
  </BgImg>
);

const SectionSubtitle = ({ children }) => (
  <Box textAlign="left" marginBottom="24px">
    <DeprecatedDetailText>{children}</DeprecatedDetailText>
  </Box>
);

const EpisodeTitle = ({ children }) => (
  <Box textAlign="left" marginBottom={["12px", "0"]}>
    <BodyText>{children}</BodyText>
  </Box>
);

const EpisodeImage = ({ imgSrc, children }) => (
  <Box
    marginBottom={["36px", "18px"]}
    borderRadius="12px"
    border="2px solid"
    borderColor={theme.colors.taro30}
    width="340px"
    height="200px !important"
    position="relative"
    style={{
      boxShadow: `0 6px 0 ${theme.colors.taro10}`,
      cursor: "pointer",
      backgroundImage: `url(${imgSrc})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  >
    {children}
  </Box>
);

const WatchButton = ({ handleClick, main }) => {
  return (
    <Box position={main ? "absolute" : "static"} bottom="-18px" right="-18px">
      <Button
        onClick={() => handleClick()}
        size={main ? "l" : "s"}
        kind={main ? "primary" : "secondary"}
        icon={<PlayIcon size="m" />}
      >
        Watch
      </Button>
    </Box>
  );
};

const MoreButton = () => (
  <Button kind="tertiary" size="s" icon={<OverflowIcon size="m" />}>
    More
  </Button>
);

const baseI18nPrefix = "directus.page_training.section";

const EpisodesSection = ({ data, index, blob }) => {
  const modalContext = React.useContext(ModalContext);

  function openVideoModal(id) {
    modalContext.showModal(ModalType.VideoModal, { youtubeID: id });
  }

  const mainEp = data.lessons[0];

  return (
    <Box margin={["30px 0", "40px 0"]} textAlign="center">
      <Container>
        <SectionTitle image={blob}>
          {index}. <Translate path={`${baseI18nPrefix}.title_${index}`} subs={data.title} />
        </SectionTitle>
        <SectionSubtitle>
          <Translate path={`${baseI18nPrefix}.description_${index}`} subs={data.description} />
        </SectionSubtitle>
        <Flex flexDirection={["column", "row"]}>
          <EpisodeImage
            imgSrc={"https://static.classdojo.com/img/resources/video-thumbs/" + data.thumbid + ".jpg"}
            handleClick={() => openVideoModal(mainEp.youtubeId)}
          >
            <WatchButton main handleClick={() => openVideoModal(mainEp.youtubeId)} />
          </EpisodeImage>
          <Space size="dt_xl" kind="inline" _dangerouslyDisableNessiePropTypes />
          <Flex flexGrow={1} flexDirection="column">
            {data.lessons.map((e, lessonIndex) => {
              const lessonI18nPrefix = `${baseI18nPrefix}.lessons_${index}`;
              const openLessonVideo = () => openVideoModal(e.youtubeId);
              return (
                <EpisodeContainer key={lessonIndex}>
                  <EpisodeTitle>
                    <Translate path={`${lessonI18nPrefix}.title_${lessonIndex + 1}`} subs={e.title} />
                  </EpisodeTitle>
                  <Flex>
                    {e.youtubeId && <WatchButton handleClick={openLessonVideo} />}
                    {e.items ? (
                      <DropDown
                        Trigger={(props) => (
                          <Box {...props}>
                            <MoreButton />
                          </Box>
                        )}
                      >
                        {e.items.map((item, itemIndex) => {
                          const itemsI18nPrefix = `${lessonI18nPrefix}.items_${lessonIndex + 1}`;
                          return (
                            <DropDownItem key={itemIndex}>
                              <a href={item.url}>
                                <Flex alignItems="center">
                                  {item.type == "download" ? <FileIcon /> : <LinkIcon />}
                                  <Space kind="inline" size="s" />
                                  <Translate path={`${itemsI18nPrefix}.text_${itemIndex + 1}`} subs={item.text} />
                                </Flex>
                              </a>
                            </DropDownItem>
                          );
                        })}
                      </DropDown>
                    ) : (
                      <Box width={114} />
                    )}
                  </Flex>
                </EpisodeContainer>
              );
            })}
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
};

export default EpisodesSection;
